import { useQuery } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/usePageTitle";
import WorkbenchLayout from "@/components/layout/workbench-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Medal, Award, FileText, Clock, Zap, MessageSquare, Building2, ClipboardList } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";

interface LeaderboardEntry {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  vbrsGenerated: number;
  copyMessagesGenerated: number;
  sponsorsAdded: number;
  cnasCreated: number;
  compositeScore: number;
  rank: number;
}

interface GlobalStats {
  totalVbrsGenerated: number;
  totalCopyMessages: number;
  totalFacts: number;
  totalHoursSaved: number;
}

// Format number to k notation (e.g., 9501 -> 9.5k)
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num.toString();
};

export default function Leaderboard() {
  usePageTitle("Leaderboard");
  const { user } = useAuth();
  
  const { data: leaderboard, isLoading } = useQuery<LeaderboardEntry[]>({
    queryKey: ["/api/leaderboard"],
    staleTime: 0, // Mark data as stale immediately
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });
  
  const { data: globalStats } = useQuery<GlobalStats>({
    queryKey: ["/api/global-stats"],
    staleTime: 0, // Mark data as stale immediately
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  const getIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return <Award className="h-5 w-5 text-gray-400" />;
  };

  const getRankBadgeColor = (rank: number) => {
    if (rank === 1) return "bg-yellow-100 text-yellow-800 border-yellow-300";
    if (rank === 2) return "bg-gray-100 text-gray-800 border-gray-300";
    if (rank === 3) return "bg-amber-100 text-amber-800 border-amber-300";
    return "bg-blue-50 text-blue-700 border-blue-200";
  };

  const getUserDisplayName = (entry: LeaderboardEntry) => {
    if (entry.firstName && entry.lastName) {
      return `${entry.firstName} ${entry.lastName}`;
    }
    if (entry.firstName) {
      return entry.firstName;
    }
    return entry.email;
  };

  return (
    <WorkbenchLayout>
      <div className="container mx-auto max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Leaderboard</h1>
          <p className="text-gray-600">See who's leading across VBRs, copy, sponsors, and CNAs!</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Top Performers</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {leaderboard && leaderboard.length > 0 ? (
                  leaderboard.map((entry) => (
                    <div
                      key={entry.userId}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        entry.userId === user?.id
                          ? "bg-primary-50 border-primary-200"
                          : "bg-white border-gray-200"
                      } hover:shadow-sm transition-shadow`}
                      data-testid={`leaderboard-entry-${entry.rank}`}
                    >
                      <div className="flex items-center space-x-4">
                        <div 
                          className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${getRankBadgeColor(entry.rank)}`}
                        >
                          <span className="font-bold text-lg">#{entry.rank}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getIcon(entry.rank)}
                          <div>
                            <p className="font-semibold text-gray-900">
                              {getUserDisplayName(entry)}
                              {entry.userId === user?.id && (
                                <span className="ml-2 px-2 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                                  You
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500">{entry.email}</p>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">{entry.compositeScore}</p>
                        <p className="text-sm text-gray-500">Score</p>
                        <div className="flex gap-4 mt-1">
                          <span className="text-xs text-gray-500">
                            <Zap className="inline h-3 w-3 mr-1" />
                            {entry.vbrsGenerated} VBRs
                          </span>
                          <span className="text-xs text-gray-500">
                            <MessageSquare className="inline h-3 w-3 mr-1" />
                            {entry.copyMessagesGenerated} Copy
                          </span>
                          <span className="text-xs text-gray-500">
                            <Building2 className="inline h-3 w-3 mr-1" />
                            {entry.sponsorsAdded ?? 0} Sponsors
                          </span>
                          <span className="text-xs text-gray-500">
                            <ClipboardList className="inline h-3 w-3 mr-1" />
                            {entry.cnasCreated ?? 0} CNAs
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Trophy className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No activity yet.</p>
                    <p className="text-sm text-gray-400 mt-2">Be the first to generate a VBR or Copy Message!</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Global Stats Section */}
        {globalStats && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Global Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-purple-400 to-purple-500 border-0 text-white hover:shadow-lg transition-all transform hover:scale-105 h-[120px]">
                <CardContent className="p-6 h-full">
                  <div className="flex items-center justify-between h-full">
                    <div className="flex flex-col justify-between h-full min-h-[72px]">
                      <p className="text-white/90 text-sm font-medium leading-tight">Total VBRs</p>
                      <p className="text-3xl font-bold text-white leading-none">
                        {formatNumber(globalStats.totalVbrsGenerated)}
                      </p>
                    </div>
                    <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-400 to-blue-500 border-0 text-white hover:shadow-lg transition-all transform hover:scale-105 h-[120px]">
                <CardContent className="p-6 h-full">
                  <div className="flex items-center justify-between h-full">
                    <div className="flex flex-col justify-between h-full min-h-[72px]">
                      <p className="text-white/90 text-sm font-medium leading-tight">Total Copy Messages</p>
                      <p className="text-3xl font-bold text-white leading-none">
                        {formatNumber(Number(globalStats.totalCopyMessages))}
                      </p>
                    </div>
                    <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-emerald-400 to-emerald-500 border-0 text-white hover:shadow-lg transition-all transform hover:scale-105 h-[120px]">
                <CardContent className="p-6 h-full">
                  <div className="flex items-center justify-between h-full">
                    <div className="flex flex-col justify-between h-full min-h-[72px]">
                      <p className="text-white/90 text-sm font-medium leading-tight">Total Facts</p>
                      <p className="text-3xl font-bold text-white leading-none">
                        {formatNumber(globalStats.totalFacts)}
                      </p>
                    </div>
                    <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-amber-400 to-amber-500 border-0 text-white hover:shadow-lg transition-all transform hover:scale-105 h-[120px]">
                <CardContent className="p-6 h-full">
                  <div className="flex items-center justify-between h-full">
                    <div className="flex flex-col justify-between h-full min-h-[72px]">
                      <p className="text-white/90 text-sm font-medium leading-tight">Total Hours Saved</p>
                      <p className="text-3xl font-bold text-white leading-none">
                        {formatNumber(globalStats.totalHoursSaved)}
                      </p>
                    </div>
                    <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </WorkbenchLayout>
  );
}